import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager
} from "firebase/firestore";
import { getAnalytics, isSupported } from "firebase/analytics";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Runtime validation for Firebase environment variables
const requiredEnvVars = {
    apiKey: "VITE_FIREBASE_API_KEY",
    authDomain: "VITE_FIREBASE_AUTH_DOMAIN",
    projectId: "VITE_FIREBASE_PROJECT_ID",
    storageBucket: "VITE_FIREBASE_STORAGE_BUCKET",
    messagingSenderId: "VITE_FIREBASE_MESSAGING_SENDER_ID",
    appId: "VITE_FIREBASE_APP_ID",
};

const missingVars = Object.entries(requiredEnvVars)
    .filter(([key]) => !firebaseConfig[key as keyof typeof firebaseConfig])
    .map(([, envVarName]) => envVarName);

if (missingVars.length > 0) {
    const errorMessage = `
    ========================================================================
    ERROR: Missing Firebase Environment Variables
    ------------------------------------------------------------------------
    The application cannot start because the following required Firebase
    environment variables are missing:
    ${missingVars.map((v) => `\n    - ${v}`).join("")}

    Please follow the instructions below to configure them:

    For Local Development:
    1. Create a file named ".env" in the root of the project.
    2. Add the missing variables to the ".env" file, like this:
       ${missingVars.map((v) => `\n       ${v}=your_value_here`).join("")}

    For GitHub Actions (Deployment):
    1. Go to your repository's "Settings" tab.
    2. Navigate to "Secrets and variables" > "Actions".
    3. Add each missing variable as a "Repository secret".

    The application will not work correctly until these variables are set.
    ========================================================================
  `;
    throw new Error(errorMessage);
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
});

// Analytics
export const analytics = isSupported().then(yes => yes ? getAnalytics(app) : null);

export const googleProvider = new GoogleAuthProvider();

export default app;
